import type { PostHogConfig } from 'posthog-js';
import type { AppEnv } from './environment';

/**
 * Config do `posthog.init()`. Módulo puro (sem `import.meta`) para ser testável no Jest.
 *
 * `capture_exceptions` substitui o `captureUncaught` / `captureUnhandledRejections` do Rollbar:
 * erros não tratados e promises rejeitadas vão para o Error Tracking do PostHog (camada essencial).
 *
 * `loaded` registra `ambiente` e `app` como super properties logo no init, então valem para todo evento:
 * os erros com o nome do Rollbar, os `$exception` do Error Tracking e os não tratados.
 */
export function buildPosthogInitConfig({
  apiHost,
  consented,
  ambiente
}: {
  apiHost: string;
  consented: boolean;
  ambiente: AppEnv;
}): Partial<PostHogConfig> {
  return {
    api_host: apiHost,
    autocapture: false, // sem captura comportamental automática (usamos eventos explícitos)
    capture_pageview: false, // pageview manual, e só com consentimento
    capture_pageleave: false,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false
    },
    disable_session_recording: true, // replay só é ligado no consentimento
    persistence: consented ? 'localStorage+cookie' : 'memory', // cookieless até aceitar
    person_profiles: 'identified_only',
    loaded: (posthog) => {
      posthog.register({ ambiente, app: 'e-commerce' });
    }
  };
}

/**
 * Chave do projeto PostHog. Homolog e produção são projetos separados, então a chave vem só de
 * `VITE_POSTHOG_KEY` (por ambiente da Vercel), sem fallback embutido: sem ela, o PostHog fica desligado.
 */
export function resolvePosthogKey({
  raw,
  isStorybook,
  warn = console.warn
}: {
  raw: unknown;
  isStorybook: boolean;
  warn?: (message: string) => void;
}): string {
  const key = String(raw ?? '').trim();
  if (!key && !isStorybook) {
    warn('[PostHog] VITE_POSTHOG_KEY não definida: monitoramento de erros desligado neste ambiente.');
  }
  return key;
}
